// Primary domain match block for the Domain Intelligence drawer.
import { Info, Sparkles, Target, Layers, PieChart, Award } from "lucide-react";
import type { DomainCoverage } from "../api/contentRegistryApi";
import { plainText, pctValue, strengthLabel, confTone } from "../utils";
import {
  ConfidenceRing,
  DomainChip,
} from "./domainIntelligenceParts";
import {
  formatConfidenceScore,
  formatSharePct,
  buildClausePreview,
  coverageStatusKey,
} from "./utils/helpers";
import {
  CLAUSE_PREVIEW_MAX,
  DOMAIN_COVERAGE_STATUS_META,
  DOMAIN_PRIMARY_LABELS,
} from "./utils/constants";

export function PrimaryDomainMatch({
  primary,
}: {
  primary: DomainCoverage | null;
}) {
  if (!primary) return <NoPrimaryState />;

  const conf = pctValue(primary.confidence);
  const tone = confTone(primary.confidence);
  const strength = strengthLabel(primary.confidence);
  const basis = buildPrimaryBasis(primary);
  const statusMeta =
    DOMAIN_COVERAGE_STATUS_META[coverageStatusKey(primary.coverageStatus)];
  const primaryPreview = buildClausePreview(
    primary.mappedClauseIds,
    CLAUSE_PREVIEW_MAX,
  );
  const secondaryPreview = buildClausePreview(
    primary.secondaryClauseIds,
    CLAUSE_PREVIEW_MAX,
  );

  return (
    <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_240px]">
      <div className="min-w-0 flex flex-col gap-3">
        <PrimaryHeader
          domainCode={primary.domainCode}
          domainName={primary.domainName}
          statusMeta={statusMeta}
        />

        {primary.domainPurpose && (
          <PurposeLine text={primary.domainPurpose} />
        )}

        <SemanticExplanation text={basis} />

        <MetricStrip primary={primary} strength={strength} />
      </div>

      <PrimaryRing conf={conf} tone={tone} strength={strength} />
    </div>
  );
}

function NoPrimaryState() {
  return (
    <div className="flex items-start gap-2 p-6 text-sm text-muted-foreground">
      <Info className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">
        {DOMAIN_PRIMARY_LABELS.noPrimary}
      </div>
    </div>
  );
}

function PrimaryHeader({
  domainCode,
  domainName,
  statusMeta,
}: {
  domainCode: string;
  domainName: string;
  statusMeta: (typeof DOMAIN_COVERAGE_STATUS_META)[keyof typeof DOMAIN_COVERAGE_STATUS_META];
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <DomainChip code={domainCode} />
        <span className="text-lg font-bold text-foreground break-words">
          {plainText(domainName)}
        </span>
      </div>
    </div>
  );
}

function PurposeLine({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-background/40 px-3 py-2">
      <span className="mt-0.5 shrink-0 rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-violet-300">
        {DOMAIN_PRIMARY_LABELS.scfPurpose}
      </span>
      <p className="text-[12px] leading-relaxed text-muted-foreground break-words">
        {plainText(text)}
      </p>
    </div>
  );
}

function SemanticExplanation({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-indigo-500 to-violet-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
          <Sparkles className="size-3" />
          {DOMAIN_PRIMARY_LABELS.aiTag}
        </span>
        <span className="text-[11px] tracking-wide text-indigo-300 font-semibold">
          {DOMAIN_PRIMARY_LABELS.aiSection}
        </span>
      </div>
      <p className="text-[12.5px] leading-relaxed text-foreground/90 break-words">
        {text}
      </p>
    </div>
  );
}

function MetricStrip({
  primary,
  strength,
}: {
  primary: DomainCoverage;
  strength: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <MetricCell
        icon={<Target className="size-3.5 text-emerald-400" />}
        value={primary.policyRequirements ?? 0}
        label={DOMAIN_PRIMARY_LABELS.primaryRefs}
        hint={DOMAIN_PRIMARY_LABELS.primaryRefsHint}
      />
      <MetricCell
        icon={<Layers className="size-3.5 text-indigo-400" />}
        value={primary.secondaryReferences ?? primary.secondaryClauseIds?.length ?? 0}
        label={DOMAIN_PRIMARY_LABELS.secondaryRefs}
        hint={DOMAIN_PRIMARY_LABELS.secondaryRefsHint}
      />
      <MetricCell
        icon={<PieChart className="size-3.5 text-cyan-400" />}
        value={formatSharePct(primary.sharePct)}
        label={DOMAIN_PRIMARY_LABELS.shareOfDocument}
        hint={DOMAIN_PRIMARY_LABELS.shareCaption}
      />
      <MetricCell
        icon={<Award className="size-3.5 text-amber-400" />}
        value={strength}
        label={DOMAIN_PRIMARY_LABELS.matchStrength}
      />
    </div>
  );
}

function MetricCell({
  icon,
  value,
  label,
  hint,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/40 px-3 py-2 min-w-0">
      <div className="flex items-baseline gap-1.5">
        {icon}
        <span className="text-lg font-bold text-foreground tabular-nums leading-none">
          {value}
        </span>
      </div>
      <div className="mt-1 text-[11px] font-semibold text-foreground break-words">
        {label}
      </div>
      {hint && (
        <div className="mt-0.5 text-[10px] text-muted-foreground break-words">
          {hint}
        </div>
      )}
    </div>
  );
}

const CLAUSE_CHIP_ACCENT = {
  emerald: "border-emerald-500/25 bg-emerald-500/[0.08] text-emerald-300",
  indigo: "border-indigo-500/25 bg-indigo-500/[0.08] text-indigo-300",
} as const;

function ClauseRow({
  label,
  preview,
  accent,
}: {
  label: string;
  preview: { visible: string[]; extra: number };
  accent: keyof typeof CLAUSE_CHIP_ACCENT;
}) {
  const chipCls = CLAUSE_CHIP_ACCENT[accent];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground shrink-0">
        {label}
      </span>
      {preview.visible.map((id) => (
        <span
          key={id}
          className={`inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono text-[10.5px] ${chipCls}`}
        >
          {id}
        </span>
      ))}
      {preview.extra > 0 && (
        <span className="text-[10.5px] text-muted-foreground">
          +{preview.extra} {DOMAIN_PRIMARY_LABELS.moreSuffix}
        </span>
      )}
    </div>
  );
}

function PrimaryRing({
  conf,
  tone,
  strength,
}: {
  conf: number | null;
  tone: ReturnType<typeof confTone>;
  strength: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 min-w-0">
      <div className="w-full text-center text-xs text-muted-foreground">
        {DOMAIN_PRIMARY_LABELS.domainMatch}
      </div>
      <ConfidenceRing value={conf} />
      <div className="w-full flex flex-col gap-1.5 text-[12px]">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">
            {DOMAIN_PRIMARY_LABELS.classificationConfidence}
          </span>
          <span className="font-semibold text-foreground tabular-nums">
            {formatConfidenceScore(conf)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">
            {DOMAIN_PRIMARY_LABELS.semanticAlignment}
          </span>
          <span className={`font-semibold ${tone.text}`}>{strength}</span>
        </div>
      </div>
    </div>
  );
}

function buildPrimaryBasis(primary: DomainCoverage): string {
  const raw = plainText(primary.coverageBasis?.trim());
  if (raw) return raw;
  const count = primary.policyRequirements ?? 0;
  return `The document aligns with ${plainText(primary.domainName)} because ${count} requirement(s) map here.`;
}
